// After placing an order: confirmed (with tracking), waiting for payment (with the pay link and
// automatic checks), or failed. Shared by grocery and food orders.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link } from '../router';
import { useT } from '../i18n';
import type { FoodOrder as Order, OrderStatus } from '../types';

export function OrderResult({ provider, name, order, addressId, onRetry, onToast }: {
  provider: string; name: string; order: Order; addressId: string; onRetry: () => void; onToast: (m: string) => void;
}) {
  const t = useT();
  const [o, setO] = useState(order);
  const [track, setTrack] = useState<OrderStatus | null>(null);
  useEffect(() => setO(order), [order.id, order.status]);

  // Payment links and UPI: check until it settles (respecting the provider's interval), for 10 minutes.
  useEffect(() => {
    if (o.status !== 'pending_payment') return;
    const started = Date.now();
    const id = setInterval(async () => {
      if (Date.now() - started > 10 * 60_000) { clearInterval(id); return; }
      try {
        const next = await api.orderPayment(provider, o.id);
        if (next.status !== 'pending_payment') { setO({ ...o, ...next }); clearInterval(id); }
      } catch (e) {
        // 404: no payment is waiting any more (another check already finished it): stop.
        if ((e as ApiError).status === 404) clearInterval(id);
      }
    }, Math.max(o.poll_ms ?? 5000, 4000));
    return () => clearInterval(id);
  }, [o.id, o.status]);

  const refresh = () => api.orderTrack(provider, o.id, addressId).then(setTrack).catch((e: ApiError) => onToast(e.message));
  useEffect(() => { if (o.status === 'confirmed' && o.id) refresh(); }, [o.status]);

  return (
    <section style="padding:16px;display:flex;flex-direction:column;gap:12px" role="status" aria-live="polite">
      {o.status === 'confirmed' && (
        <div class="card" style="padding:18px;display:flex;flex-direction:column;gap:8px">
          <div class="display" style="font-weight:800;font-size:22px">{t('food.placed', { app: name })}</div>
          <div class="muted">{[o.id && t('food.orderId', { id: o.id }), (track?.eta || o.eta) && t('food.eta', { eta: track?.eta || o.eta || '' })].filter(Boolean).join(' · ')}</div>
          {(track?.message || o.message) && <div>{track?.message || o.message}</div>}
          <button class="btn btn-outline" onClick={refresh}>{t('food.refresh')}</button>
        </div>
      )}
      {o.status === 'pending_payment' && (
        <div class="card" style="padding:18px;display:flex;flex-direction:column;gap:10px">
          <div class="display" style="font-weight:800;font-size:20px">{t('order.payTitle')}</div>
          <div class="muted">{t('food.payBody', { app: name })}</div>
          {o.message && <div>{o.message}</div>}
          {o.pay_url
            ? <a class="btn btn-primary" href={o.pay_url} target="_blank" rel="noopener">{t('order.payNow')}</a>
            : <div class="hint-box">{t('order.payInApp2', { app: name })}</div>}
          <div class="small muted">{t('food.waiting')}</div>
        </div>
      )}
      {o.status === 'failed' && (
        <div class="card" style="padding:18px;display:flex;flex-direction:column;gap:10px" role="alert">
          <div class="display" style="font-weight:800;font-size:20px">{t('food.failed')}</div>
          {o.message && <div class="muted">{o.message}</div>}
          <div class="small muted" style="line-height:1.5">{t('order.checkFirst', { app: name })}</div>
          <button class="btn btn-outline" onClick={onRetry}>{t('common.tryAgain')}</button>
        </div>
      )}
      {o.status !== 'failed' && <div class="small muted" style="line-height:1.5;text-align:center">{t('order.cancelInApp', { app: name, id: o.id || '—' })}</div>}
      <Link href="/" class="btn-text" style="text-align:center">{t('nav.home')}</Link>
    </section>
  );
}
